import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ProjectComponent = ({callback }) => {
  const [projects, setProjects] = useState([]);
  const [devChannels, setDevChannels] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('Not Started');
  const [devChannelId, setDevChannelId] = useState('');
  const [projectStartDate, setProjectStartDate] = useState('');
  const [projectEndDate, setProjectEndDate] = useState('');
  const [notes, setNotes] = useState(['']);
  const [completed, setCompleted] = useState(false);
  const [jiraLink, setJiraLink] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [textColor, setTextColor] = useState('#000000');
  const [editingId, setEditingId] = useState(null);

  // Fetch all projects and devChannels on component mount
  useEffect(() => {
    fetchProjects();
    fetchDevChannels();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchDevChannels = async () => {
    try {
      const response = await axios.get('/api/devChannels');
      setDevChannels(response.data);
    } catch (error) {
      console.error('Error fetching devChannels:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      title,
      description,
      status,
      devChannelId,
      projectStartDate,
      projectEndDate,
      notes,
      completed,
      jiraLink,
      backgroundColor,
      textColor,
    };

    try {
      if (editingId) {
        // Update existing project
        await axios.put(`/api/projects/${editingId}`, data);
        setEditingId(null);
      } else {
        // Create new project
        await axios.post('/api/projects', data);
      }
      fetchProjects();
      // Reset form fields
      setTitle('');
      setDescription('');
      setStatus('Not Started');
      setDevChannelId('');
      setProjectStartDate('');
      setProjectEndDate('');
      setNotes(['']);
      setCompleted(false);
      setJiraLink('');
      setBackgroundColor('#ffffff');
      setTextColor('#000000');
      
      if (callback) {
        callback();
      }
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  const handleEdit = (project) => {
    setTitle(project.title);
    setDescription(project.description);
    setStatus(project.status);
    setDevChannelId(project.devChannelId?._id || '');
    setProjectStartDate(project.projectStartDate ? new Date(project.projectStartDate).toISOString().split('T')[0] : '');
    setProjectEndDate(project.projectEndDate ? new Date(project.projectEndDate).toISOString().split('T')[0] : '');
    setNotes(project.notes || ['']);
    setCompleted(project.completed || false);
    setJiraLink(project.jiraLink || '');
    setBackgroundColor(project.backgroundColor || '#ffffff');
    setTextColor(project.textColor || '#000000');
    setEditingId(project._id);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/projects/${id}`);
      fetchProjects();
      if (callback) {
        callback();
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleNoteChange = (index, value) => {
    const newNotes = [...notes];
    newNotes[index] = value;
    setNotes(newNotes);
  };

  const addNote = () => setNotes([...notes, '']);
  const removeNote = (index) => setNotes(notes.filter((_, i) => i !== index));

  return (
    <div>
      <h2>Projects</h2>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
        <input
          type="text"
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <input
          type="text"
          placeholder="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        />
        <select
          value={devChannelId}
          onChange={(e) => setDevChannelId(e.target.value)}
        >
          <option value="">Select Dev Channel</option>
          {devChannels.map((channel) => (
            <option key={channel._id} value={channel._id}>
              {channel.name}
            </option>
          ))}
        </select>
        <input
          type="date"
          placeholder="Project Start Date"
          value={projectStartDate}
          onChange={(e) => setProjectStartDate(e.target.value)}
        />
        <input
          type="date"
          placeholder="Project End Date"
          value={projectEndDate}
          onChange={(e) => setProjectEndDate(e.target.value)}
        />
        <div>
          <label>Notes:</label>
          {notes.map((note, index) => (
            <div key={index}>
              <input
                type="text"
                placeholder={`Note ${index + 1}`}
                value={note}
                onChange={(e) => handleNoteChange(index, e.target.value)}
              />
              <button type="button" onClick={() => removeNote(index)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" onClick={addNote}>Add Note</button>
        </div>
        <label>
          Completed:
          <input
            type="checkbox"
            checked={completed}
            onChange={(e) => setCompleted(e.target.checked)}
          />
        </label>
        <input
          type="text"
          placeholder="Jira Link"
          value={jiraLink}
          onChange={(e) => setJiraLink(e.target.value)}
        />
        <label>
          Background Color:
          <input
            type="color"
            value={backgroundColor}
            onChange={(e) => setBackgroundColor(e.target.value)}
          />
        </label>
        <label>
          Text Color:
          <input
            type="color"
            value={textColor}
            onChange={(e) => setTextColor(e.target.value)}
          />
        </label>
        <button type="submit">{editingId ? 'Update' : 'Create'}</button>
      </form>
      <ul>
        {projects.filter((project) => !project.completed).map((project) => (
          <li key={project._id}>
            <strong>{project.title}</strong> - {project.description} (
            {project.status}) - DevChannel: {project.devChannelId?.name || 'N/A'}
            - Start Date: {project.projectStartDate ? new Date(project.projectStartDate).toLocaleDateString() : 'N/A'}
            - End Date: {project.projectEndDate ? new Date(project.projectEndDate).toLocaleDateString() : 'N/A'}
            - Completed: {project.completed ? 'Yes' : 'No'}
            <br />
            Notes: {project.notes?.join(', ') || 'None'}
            <br />
            Jira Link: <a href={project.jiraLink}>{project.jiraLink}</a>
            <br />
            Background Color: <span style={{ backgroundColor: project.backgroundColor }}>{project.backgroundColor}</span>
            <br />
            Text Color: <span style={{ color: project.textColor }}>{project.textColor}</span>
            <button onClick={() => handleEdit(project)}>Edit</button>
            <button onClick={() => handleDelete(project._id)}>Delete</button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ProjectComponent;
